import apiClient from './api';
import type { PackageCategory } from '@/types';

export type PackageType = 'apk' | 'os' | 'fw';

export interface Package {
  id: string;
  package_name: string;
  type: PackageType;
  display_name: string;
  file: string;
  version: string;
  file_name: string;
  file_size_bytes: number;
  checksum: string;
  description: string;
  machine_types: string[];
  ats_ids: string[];
  created_at: string;
  updated_at: string;
  /** Set when the package is soft-deleted: file removed, usable in no new rollout. Null/absent = live. */
  deleted_at?: string | null;
}

export interface PackageCatalogEntry {
  display_name: string;
  type: PackageType;
  package_name: string;
  category: PackageCategory;
  sort_order: number;
  /**
   * Whether this identity may be selected in a rollout of its matching type.
   * Replaces the old `rollout_types` list — the rollout-to-package type mapping
   * already provides the type relationship.
   */
  rollout_selectable: boolean;
}

export interface CreatePackageRequest {
  package_name: string;
  type: PackageType;
  file: string;
  version: string;
  file_name?: string;
  file_size_bytes?: number;
  checksum?: string;
  description?: string;
  machine_types?: string[];
  ats_ids?: string[];
}

export interface UpdatePackageRequest {
  package_name: string;
  type: PackageType;
  version: string;
  description?: string;
  machine_types?: string[];
  ats_ids?: string[];
}

export interface PackageUploadURLRequest {
  items: Array<{
    package_name: string;
    type: PackageType;
    version: string;
    file_name: string;
    content_type: string;
    file_size_bytes: number;
  }>;
}

export interface PackageUploadURLItem {
  package_name: string;
  type: PackageType;
  version: string;
  /** overwrite when the filename matches the current object, new_object otherwise. */
  mode?: 'overwrite' | 'new_object';
  upload_url: string;
  method: string;
  headers: Record<string, string>;
  file: string;
  bucket: string;
  object: string;
  expires_at: string;
}

export interface PackageUploadURLResponse {
  items: PackageUploadURLItem[];
}

export interface ChangeFileUploadURLRequest {
  package_name: string;
  type: PackageType;
  file_name: string;
  content_type: string;
}

export interface CompleteFileChangeRequest {
  file_name: string;
}

export interface PackageDownloadURL {
  url: string;
  file_name: string;
  file_size_bytes: number;
  expires_at: string;
}

/** Rollout that references a package, returned in the 409 body when delete is blocked. */
export interface RolloutRef {
  id: string;
  name: string;
  status: 'draft' | 'active' | 'archived';
}

/** Body of the 409 Conflict response when deleting a package still used by rollouts. */
export interface PackageInUseError {
  message: string;
  rollouts: RolloutRef[];
}

export interface PackageRolloutsResponse {
  rollouts: RolloutRef[];
}

const base = '/admin/software-update/packages';
const catalogBase = '/admin/software-update/package-catalog';

export const softwareUpdatePackageService = {
  list: async (): Promise<Package[]> => {
    return apiClient.get<Package[]>(base);
  },
  listCatalog: async (): Promise<PackageCatalogEntry[]> => {
    return apiClient.get<PackageCatalogEntry[]>(catalogBase);
  },
  get: async (id: string): Promise<Package> => {
    return apiClient.get<Package>(`${base}/${id}`);
  },
  listRollouts: async (id: string): Promise<PackageRolloutsResponse> => {
    return apiClient.get<PackageRolloutsResponse>(`${base}/${id}/rollouts`);
  },
  create: async (data: CreatePackageRequest): Promise<Package> => {
    return apiClient.post<Package>(base, data);
  },
  update: async (id: string, data: UpdatePackageRequest): Promise<Package> => {
    return apiClient.put<Package>(`${base}/${id}`, data);
  },
  delete: async (id: string): Promise<void> => {
    return apiClient.delete(`${base}/${id}`);
  },
  createUploadURLs: async (data: PackageUploadURLRequest): Promise<PackageUploadURLResponse> => {
    return apiClient.post<PackageUploadURLResponse>(`${base}/upload-urls`, data);
  },
  changeFileUploadURL: async (
    id: string,
    data: ChangeFileUploadURLRequest
  ): Promise<PackageUploadURLItem> => {
    return apiClient.post<PackageUploadURLItem>(`${base}/${id}/change-file-upload-url`, data);
  },
  completeFileChange: async (id: string, data: CompleteFileChangeRequest): Promise<Package> => {
    return apiClient.post<Package>(`${base}/${id}/change-file-complete`, data);
  },
  getDownloadURL: async (id: string): Promise<PackageDownloadURL> => {
    return apiClient.get<PackageDownloadURL>(`${base}/${id}/download-url`);
  },
  uploadFile: async (
    upload: PackageUploadURLItem,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<void> => {
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(upload.method || 'PUT', upload.upload_url);
      Object.entries(upload.headers || {}).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });
      if (!upload.headers?.['Content-Type'] && file.type) {
        xhr.setRequestHeader('Content-Type', file.type);
      }
      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable || !onProgress) return;
        onProgress(Math.round((event.loaded / event.total) * 100));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else if (xhr.status === 412) reject(new Error('A file with this name already exists'));
        else reject(new Error(`Upload failed with status ${xhr.status}`));
      };
      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(file);
    });
  },
};

export default softwareUpdatePackageService;
